import { AvatarQueryKeys } from '../../queryKeys';
import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { fetchAvatars } from './service';
import { UseAvatarsProps, UseAvatarsQueryKey } from './props';
import {
  Avatar,
  GenericError,
  PaginatedResponse,
} from '@starsoft/common/models';
import { AxiosError, AxiosInstance } from 'axios';
import {
  useAxiosInstance,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { useMemo } from 'react';

/**
 * Custom hook to fetch paginated avatar data.
 *
 * @param {UseAvatarsProps} options - Configuration options for the query.
 * @param {boolean} [options.enabled=true] - Flag to enable or disable the query.
 * @param {boolean} [options.refetchOnMount=true] - Flag to refetch data when the component mounts.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {boolean} return.isLoading - Indicates if the query is currently loading.
 * @returns {InfiniteData<PaginatedResponse<Avatar>>} return.data - The fetched paginated avatar data.
 * @returns {AxiosError<GenericError>} return.error - Error object if the query fails.
 * @returns {Function} return.refetch - Function to manually refetch the data.
 * @returns {Avatar[]} return.avatars - Flattened array of avatar data from all pages.
 */
export function useAvatars(options?: UseAvatarsProps) {
  // Destructure options with default values
  const { enabled = true, refetchOnMount = true } = options ?? {};

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Define the query key for caching and refetching purposes
  const queryKey: UseAvatarsQueryKey = [
    AvatarQueryKeys.Primary,
    {
      limit: 20,
    },
  ];

  // Get functions to handle pagination
  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  // Reset the infinite query state
  useInfiniteQueryReset(queryKey);

  // Use the useInfiniteQuery hook to fetch paginated avatar data
  const { isPending, data, error, refetch, fetchStatus, ...rest } =
    useInfiniteQuery<
      PaginatedResponse<Avatar>,
      AxiosError<GenericError>,
      InfiniteData<PaginatedResponse<Avatar>>,
      UseAvatarsQueryKey
    >({
      queryKey,
      queryFn: queryContext => fetchAvatars(queryContext, axiosInstance),
      enabled,
      refetchOnWindowFocus: false,
      refetchOnMount,
      getNextPageParam,
      getPreviousPageParam,
      initialPageParam: 1,
      staleTime: 5 * 1000, // Data is considered fresh for 5 seconds
    });

  // Memoize the flattened array of avatar data from all pages
  const avatars = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  // Return the query state and functions
  return {
    ...rest,
    isLoading: isPending && fetchStatus !== 'idle',
    data,
    error,
    refetch,
    avatars,
  };
}
